import type { CreateInviteeRequest, CoApplicant } from '../types/invitee'
import {
  INCOME_MAX_DIGITS_AFTER_DECIMAL,
  INCOME_MAX_DIGITS_BEFORE_DECIMAL,
} from './inviteeValidators'

/**
 * Format SSN for display: XXX-XX-XXXX (mask first 5 digits, show last 4)
 * @param ssn - SSN string (full or last 4 digits)
 * @returns Masked SSN string
 */
export function formatSSNForDisplay(ssn: string): string {
  if (!ssn) return ''
  const digits = String(ssn).replace(/\D/g, '')
  const last4 = digits.slice(-4)
  if (last4.length === 0) return ''
  return `XXX-XX-${last4}`
}

/**
 * Format phone number for display: (xxx) xxx-xxxx
 * @param phone - Phone number string
 * @returns Formatted phone number
 */
export function formatPhoneNumber(phone: string): string {
  if (!phone) return ''
  
  // Remove all non-numeric characters
  const digits = String(phone).replace(/\D/g, '')
  
  if (digits.length === 10) {
    // Format as (XXX) XXX-XXXX
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  } else if (digits.length === 11) {
    // Format as +X (XXX) XXX-XXXX
    return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  } else if (digits.length === 12 || digits.length === 13) {
    // Format as +XX (XXX) XXX-XXXX or +XXX (XXX) XXX-XXXX
    const countryCode = digits.length === 12 ? digits.slice(0, 2) : digits.slice(0, 3)
    const areaCode = digits.length === 12 ? digits.slice(2, 5) : digits.slice(3, 6)
    const firstPart = digits.length === 12 ? digits.slice(5, 8) : digits.slice(6, 9)
    const lastPart = digits.length === 12 ? digits.slice(8) : digits.slice(9)
    return `+${countryCode} (${areaCode}) ${firstPart}-${lastPart}`
  }
  
  return phone
}

/**
 * Remove non-numeric characters from phone number
 * @param phone - Phone number string
 * @returns Phone number with only digits
 */
export function cleanPhoneNumber(phone: string): string {
  if (!phone) return ''
  return phone.replace(/\D/g, '')
}

/**
 * Format currency for display: $XXX.XX
 * @param amount - Amount string or number
 * @returns Formatted currency string
 */
export function formatCurrency(amount: string | number): string {
  if (!amount) return ''
  
  const num = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.-]/g, '')) : amount
  if (isNaN(num)) return ''
  
  return `$${num.toFixed(2)}`
}

/** Integer digits at or below this count use comma grouping; above → scientific (Angular CurrencyPipe / JS Number style). */
export const INVITE_INCOME_MAX_GROUPING_DIGITS = INCOME_MAX_DIGITS_BEFORE_DECIMAL

/** Digits + at most one `.` — same first step as Angular `appAmount`. */
export function sanitizeInviteIncomeRawInput(raw: string): string {
  let val = raw.replace(/[^0-9.]+/g, '')
  const parts = val.split('.')
  if (parts.length > 2) {
    val = parts[0] + '.' + parts.slice(1).join('')
  }

  const dot = val.indexOf('.')
  const intPart = dot === -1 ? val : val.slice(0, dot)
  const fracPart = dot === -1 ? '' : val.slice(dot + 1)
  const cappedInt = intPart.replace(/\D/g, '').slice(0, INCOME_MAX_DIGITS_BEFORE_DECIMAL)
  const fracDigits = fracPart.replace(/\D/g, '').slice(0, INCOME_MAX_DIGITS_AFTER_DECIMAL)
  const wantTrailingDot = dot !== -1 && fracPart.length === 0
  if (wantTrailingDot) return `${cappedInt}.`
  return fracDigits.length > 0 ? `${cappedInt}.${fracDigits}` : cappedInt
}

/**
 * US-style thousands separators on the integer digit run only.
 * Angular `CurrencyPipe` uses `Number`, which corrupts values past ~16 digits; we group in string space so every typed digit stays correct.
 */
function groupIntegerDigitsUsLocale(integerDigits: string): string {
  const onlyDigits = integerDigits.replace(/\D/g, '')
  if (!onlyDigits) return ''
  const normalized = onlyDigits.replace(/^0+(?=\d)/, '') || '0'
  let out = ''
  let n = 0
  for (let i = normalized.length - 1; i >= 0; i--) {
    if (n === 3) {
      out = ',' + out
      n = 0
    }
    out = normalized[i]! + out
    n++
  }
  return out
}

function integerDigitRunOnly(intRaw: string): string {
  return intRaw.replace(/\D/g, '')
}

/**
 * Integer part for display: commas up to {@link INVITE_INCOME_MAX_GROUPING_DIGITS}, then scientific like Angular/Number.
 */
function formatInviteIncomeIntegerDisplay(intRaw: string): string {
  const only = integerDigitRunOnly(intRaw)
  if (!only) return ''
  const normalized = only.replace(/^0+(?=\d)/, '') || '0'
  const significant = normalized === '' ? '0' : normalized
  if (significant.length <= INVITE_INCOME_MAX_GROUPING_DIGITS) {
    return groupIntegerDigitsUsLocale(intRaw)
  }
  const n = Number(significant)
  if (!Number.isFinite(n)) {
    return groupIntegerDigitsUsLocale(significant.slice(0, INVITE_INCOME_MAX_GROUPING_DIGITS))
  }
  return n.toExponential().replace(/e/i, 'E')
}

/**
 * Display-only formatting for invite "Income Total". Store {@link sanitizeInviteIncomeRawInput} output in RHF (digits + one `.`, no commas) and pass it here for the input `value`.
 */
export function formatInviteIncomeWhileTyping(sanitized: string): string {
  if (!sanitized) return ''
  if (sanitized.includes('.')) {
    const values = sanitized.split('.')
    const intRaw = values[0] ?? ''
    const frac = (values[1] ?? '').substring(0, INCOME_MAX_DIGITS_AFTER_DECIMAL)
    const intShown = intRaw ? formatInviteIncomeIntegerDisplay(intRaw) : '0'
    const scientific = /[eE][+-]?\d+$/.test(intShown)
    if (scientific) {
      return frac.length > 0 ? `${intShown}.${frac}` : intShown
    }
    return intShown + '.' + frac
  }
  return formatInviteIncomeIntegerDisplay(sanitized)
}

/**
 * Remove currency symbols and format for API
 * @param amount - Amount string
 * @returns Numeric string
 */
export function cleanCurrency(amount: string): string {
  if (!amount) return ''

  const stripped = amount.trim().replace(/\$/g, '').replace(/,/g, '').replace(/\s/g, '')
  if (/^[+-]?(?:\d+\.?\d*|\d*\.?\d+)[eE][+-]?\d+$/.test(stripped)) {
    const n = Number(stripped)
    return Number.isFinite(n) ? String(n) : ''
  }

  const cleaned = stripped.replace(/[^0-9.]/g, '')

  const parts = cleaned.split('.')
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('')
  }

  return cleaned
}

/**
 * Format date to YYYY-MM-DD for API
 * @param date - Date string or Date object
 * @returns Formatted date string (YYYY-MM-DD)
 */
export function formatDateForAPI(date: string | Date): string {
  if (!date) return ''
  
  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateObj.getTime())) return ''
  
  const year = dateObj.getFullYear()
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')
  
  return `${year}-${month}-${day}`
}

/**
 * Format date for display: MM/DD/YYYY
 * @param date - Date string (YYYY-MM-DD), Date object, or Unix timestamp (number)
 * @returns Formatted date string (MM/DD/YYYY)
 */
export function formatDateForDisplay(date: string | Date | number | null | undefined): string {
  if (!date) return ''
  
  let dateObj: Date
  
  // Handle Unix timestamp (number in seconds)
  if (typeof date === 'number') {
    // Check if it's a Unix timestamp in seconds (10 digits) or milliseconds (13 digits)
    if (date.toString().length === 10) {
      dateObj = new Date(date * 1000) // Convert seconds to milliseconds
    } else {
      dateObj = new Date(date) // Already in milliseconds
    }
  } else if (typeof date === 'string') {
    // Handle string dates - could be ISO format or "YYYY-MM-DD HH:mm:ss"
    if (date.includes(' ')) {
      // Format: "2026-01-19 04:53:51"
      dateObj = new Date(date.replace(' ', 'T'))
    } else {
      dateObj = new Date(date)
    }
  } else {
    dateObj = date
  }
  
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return ''
  
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')
  const year = dateObj.getFullYear()
  
  return `${month}/${day}/${year}`
}

/**
 * Format date/time for display: MM/DD/YYYY hh:mm:ss a (Angular parity)
 * @param date - Date string, Date object, or Unix timestamp (number)
 * @returns Formatted date/time string
 */
export function formatDateTimeForDisplay(date: string | Date | number | null | undefined): string {
  if (!date) return ''

  let dateObj: Date
  if (typeof date === 'number') {
    dateObj = new Date(date.toString().length === 10 ? date * 1000 : date)
  } else if (typeof date === 'string') {
    dateObj = date.includes(' ') ? new Date(date.replace(' ', 'T')) : new Date(date)
  } else {
    dateObj = date
  }

  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return ''

  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')
  const year = dateObj.getFullYear()

  let hours = dateObj.getHours()
  const minutes = String(dateObj.getMinutes()).padStart(2, '0')
  const seconds = String(dateObj.getSeconds()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12
  hours = hours ? hours : 12

  return `${month}/${day}/${year} ${String(hours).padStart(2, '0')}:${minutes}:${seconds} ${ampm}`
}

/**
 * Capitalize first letter of each word (title case)
 * @param text - Text string
 * @returns Capitalized text
 */
export function titleCase(text: string): string {
  if (!text) return ''
  return text
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Validate and format data for API request
 * Based on validateData function from documentation
 * @param data - Form data object
 * @returns Formatted data ready for API
 */
export function validateData(data: CreateInviteeRequest): CreateInviteeRequest {
  const formatted: CreateInviteeRequest = { ...data }

  // Trim all string values like Angular's getFilterValue()
  Object.keys(formatted).forEach((key) => {
    const value = formatted[key as keyof CreateInviteeRequest]
    if (typeof value === 'string' && value !== '') {
      ;(formatted as any)[key] = value.trim()
    }
  })

  // Remove non-numeric characters from phone numbers
  if (formatted.mobile_no) {
    formatted.mobile_no = cleanPhoneNumber(formatted.mobile_no)
  }

  // Format dates to YYYY-MM-DD
  if (formatted.lease_start_date) {
    formatted.lease_start_date = formatDateForAPI(formatted.lease_start_date)
  }

  // Remove currency symbols from amounts
  if (formatted.rent_amount) {
    formatted.rent_amount = cleanCurrency(formatted.rent_amount)
  }
  if (formatted.income) {
    formatted.income = cleanCurrency(formatted.income)
  }

  // Ensure email is lowercase (after trimming)
  if (formatted.email) {
    formatted.email = formatted.email.toLowerCase()
  }

  // Process co-applicants
  if (formatted.co_applicant && formatted.co_applicant.length > 0) {
    formatted.co_applicant = formatted.co_applicant.map((coApp) => {
      const formattedCoApp: CoApplicant = { ...coApp }

      // Trim all string values like Angular's getFilterValue()
      Object.keys(formattedCoApp).forEach((key) => {
        const value = formattedCoApp[key as keyof CoApplicant]
        if (typeof value === 'string' && value !== '') {
          ;(formattedCoApp as any)[key] = value.trim()
        }
      })

      // Remove non-numeric characters from phone numbers
      if (formattedCoApp.mobile_no) {
        formattedCoApp.mobile_no = cleanPhoneNumber(formattedCoApp.mobile_no)
      }

      // Remove currency symbols from income
      if (formattedCoApp.income) {
        formattedCoApp.income = cleanCurrency(formattedCoApp.income)
      }

      // Ensure email is lowercase (after trimming)
      if (formattedCoApp.email) {
        formattedCoApp.email = formattedCoApp.email.toLowerCase()
      }

      // Remove empty string values from co-applicant (but keep required fields)
      // Only remove truly empty optional fields
      Object.keys(formattedCoApp).forEach((key) => {
        const value = formattedCoApp[key as keyof CoApplicant]
        // Keep required fields even if empty, but remove undefined/null/empty strings for optional fields
        if (value === '' || value === null || value === undefined) {
          // Keep these fields even if empty: first_name, last_name, mobile_no, relationship_type, living_unit, is_approved_require, income_verified
          // Only remove optional fields: email, income
          if (key === 'email' || (key === 'income' && !formattedCoApp.income)) {
            delete formattedCoApp[key as keyof CoApplicant]
          }
        }
      })

      return formattedCoApp
    })
  } else {
    // Remove co_applicant array if empty
    delete formatted.co_applicant
  }

  // Remove empty string values from main form (except middle_name which is always removed)
  Object.keys(formatted).forEach((key) => {
    const value = formatted[key as keyof CreateInviteeRequest]
    if (value === '' || value === null || value === undefined) {
      // Don't delete these fields even if empty
      const preservedFields = [
        'unit_number', 
        'income', 
        'popid', 
        'co_applicant', 
        'property_id',
        'first_name',
        'last_name',
        'rent_amount',
        'lease_start_date',
        'income_verified',
        'crime',
        'approved_by_liberty',
        'allow_to_movein'
      ]
      
      if (!preservedFields.includes(key)) {
        delete formatted[key as keyof CreateInviteeRequest]
      }
    }
  })

  return formatted
}

/**
 * Validate and format invite update payload (Angular /api/update-invitation parity)
 * @param data - Update payload from Edit/Add Co-Applicant dialog
 * @returns Formatted payload
 */
export function validateInvitationUpdateData(data: Record<string, any>): Record<string, any> {
  const formatted: Record<string, any> = { ...data }

  if (formatted.mobile_no) {
    formatted.mobile_no = cleanPhoneNumber(formatted.mobile_no)
  }
  if (formatted.email) {
    formatted.email = String(formatted.email).trim().toLowerCase()
  }
  if (formatted.first_name) formatted.first_name = String(formatted.first_name).trim()
  if (formatted.last_name) formatted.last_name = String(formatted.last_name).trim()

  if (formatted.income) {
    formatted.income = cleanCurrency(String(formatted.income))
  }

  // If income not verified, clear income
  if (formatted.income_verified === '0') {
    delete formatted.income
  }

  Object.keys(formatted).forEach((key) => {
    const v = formatted[key]
    if (v === '' || v === null || v === undefined) {
      delete formatted[key]
    }
  })

  return formatted
}

/**
 * Remove empty fields from object
 * @param obj - Object to clean
 * @returns Cleaned object
 */
export function removeEmptyFields<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const cleaned: Partial<T> = {}
  
  Object.keys(obj).forEach((key) => {
    const value = obj[key]
    if (value !== '' && value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          cleaned[key as keyof T] = value as any
        }
      } else {
        cleaned[key as keyof T] = value as any
      }
    }
  })
  
  return cleaned
}
